import * as Sentry from "@sentry/react";

/**
 * Initialise Sentry.
 * Call this ONCE at the top of index.js before rendering the React tree.
 *
 * Required env var:
 *   REACT_APP_SENTRY_DSN  – the DSN copied from sentry.io → Project → SDK Setup
 *
 * Optional env var:
 *   REACT_APP_APP_VERSION – e.g. "1.2.0" set by your CI pipeline
 */
export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;

  // Don't initialise in local dev unless the DSN is explicitly provided.
  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV, // "development" | "production"
    release: process.env.REACT_APP_APP_VERSION || "pahadi-lingo@unknown",

    // Capture 100% of errors. For traces, sample at 10% in production to
    // avoid quota burn – raise it once you have a baseline.
    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,

    // Record a short replay (30 s before → 30 s after) only for sessions that
    // contain an error. Zero cost for normal sessions.
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0, // don't record all sessions

    integrations: [
      Sentry.replayIntegration({
        // Mask all user-typed text and block media by default to stay GDPR-safe.
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.browserTracingIntegration(),
    ],
  });
}

/**
 * Call this inside an onAuthStateChanged listener to attach the Firebase user
 * to every subsequent Sentry event.
 *
 * @param {import("firebase/auth").User | null} user
 */
export function setSentryUser(user) {
  if (user) {
    Sentry.setUser({
      id: user.uid,
      email: user.email,
      username: user.displayName || undefined,
    });
  } else {
    // User signed out – clear PII from future events.
    Sentry.setUser(null);
  }
}
